import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { IProductDetails } from '../../models/ProductDetails';
import styled from '@mui/styles/styled';
import { useTranslation } from 'react-i18next';
import { ButtonWithChevronWithLink } from '../ButtonWithChevron/ButtonWithChevron';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { apiUserDownloads } from '../../resources/api/apiUserDownloads';

import { IUserExample } from '../../resources/models/IUserExample';

interface IProps {
  productDetails: Omit<IProductDetails, 'plans'>;
  userExample: IUserExample;
}

const Image = styled('img')({
  width: '100%',
});

const CardHeader = styled('div')({
  padding: '20px 16px',
});

const CardFooter = styled('div')({
  padding: '16px 16px 20px',
});

const FlexPaper = styled(Paper)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const FlexSpaceFiller = styled('div')({
  flex: 1,
});

const CompatibleVersionsContainer = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const { useGetFile } = apiUserDownloads;

export const ProductSingleContent: FC<IProps> = ({ productDetails, userExample }) => {
  const { t } = useTranslation();
  const [actualImageUrl, setActualImageUrl] = useState<string | null>(null);

  const { execute: download } = useGetFile();

  useEffect(() => {
    (async () => {
      const result = await download(userExample.image);
      setActualImageUrl(result.url);
    })();
  }, [userExample.image]);

  const examplesUrl = AuthenticatedRoutes.UserDownloadsProductExamples(
    productDetails.id,
    userExample.example.versions[0]
  );

  return (
    <Grid item xs={12} md={6} lg={3}>
      <FlexPaper>
        <CardHeader>
          <Typography variant="h4" textAlign="center">
            {userExample.example.title}
          </Typography>

          <Typography variant="subtitle1" textAlign="center">
            {t('userOverview.exampleProject')}
          </Typography>
        </CardHeader>

        {actualImageUrl ? <Image src={actualImageUrl} /> : null}

        <Box p={3} pb={0}>
          <Typography paragraph marginBottom={0}>
            {t('userOverview.compatibleWith')}
            <CompatibleVersionsContainer>
              {userExample.example.versions.map((version) => (
                <Chip component="span" key={version} label={version} color="primary" />
              ))}
            </CompatibleVersionsContainer>
          </Typography>
        </Box>

        <FlexSpaceFiller />

        <CardFooter>
          <ButtonWithChevronWithLink to={examplesUrl}>{t('userOverview.goToExamples')}</ButtonWithChevronWithLink>
        </CardFooter>
      </FlexPaper>
    </Grid>
  );
};
